import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addDays } from "date-fns";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  Select,
  MenuItem,
  FormControl,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import {
  DarkMode,
  LightMode,
  Menu,
  Settings,
  Logout,
} from "@mui/icons-material";
import { useDateRangeContext } from 'contexts/DateRangeContext';
import { 
  setMode, 
  // setTimeMode, 
  setLogout, 
  setCrewFlights,   
} from "state";
import { DASHBOARD_TIMER_INTERVAL, NAV_HEIGHT } from 'state/constants';
import { fetchLegMembersByCrewCode } from 'state/api';
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";
import DatePickerRange from "components/DatePickerRange";
import FlexBetween from "components/FlexBetween";


const Navbar = ({ onOpenMobile }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timeMode = useSelector((state) => state.auth.timemode);
  const user = useSelector((state) => state.auth.user);
  const crewCode = user?.crewCode;
  const fullName = `${user?.crewName ?? 'Unknown'}`.toUpperCase();
    
  const theme = useTheme();
  const backgroundAlt = theme.palette.background.alt;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
  const { startDate, endDate } = useDateRangeContext();

  const [loading, setLoading] = useState(false);
  

  useEffect(() => {
    const handleCrewFlights = async () => {
      if (!navigator.onLine) return; // keep cached flights when offline
      try {
        setLoading(true);

        const fromDate = startDate;
        const tomorrow = addDays(new Date(), 1);
        const toDate = endDate && new Date(endDate) > tomorrow
          ? tomorrow
          : endDate;

        if (crewCode) {
          const result = await fetchLegMembersByCrewCode(crewCode, fromDate, toDate);
          dispatch(setCrewFlights({ crewFlights: result }));
        }
      } catch (err) {
        dispatch(setCrewFlights({ crewFlights: [] }));
      } finally {
        setLoading(false);
      }
    }

    handleCrewFlights();
    
    const interval = setInterval(() => {
      handleCrewFlights();
    }, DASHBOARD_TIMER_INTERVAL); 
  
    return () => clearInterval(interval);  
  }, [
    dispatch, 
    crewCode, 
    timeMode, 
    startDate, 
    endDate,
  ]);

  const handleLogOut = useCallback(() => {
    dispatch(setLogout());
  }, [dispatch]);


  return (
    <FlexBetween
      position="sticky"
      top={0}
      zIndex={99}
      height={NAV_HEIGHT}
      px={2}
      backgroundColor={backgroundAlt}
    >
      <FlexBetween
        gap={1}
        sx={{ "& .datepicker": { maxWidth: "6.6rem" } }}
      >    
        {isMobile && (
          <IconButton onClick={onOpenMobile}>
            <Menu />
          </IconButton>
        )}

        <DatePickerRange />        
      </FlexBetween>

      <FlexBetween gap={1}>
        {loading ? (
          <Box 
            display="flex" 
            alignItems="center" 
            width={25}
            height={25}
          >
            <CircularProgress size={20}/>
          </Box>
        ) : (
          <Box 
            display="flex" 
            alignItems="center"
            // onClick={() => dispatch(setTimeMode())}
          >
            <img
              alt="Flag"
              width={25}
              height={25}              
              src={timeMode === "UTC" ? UnitedNationsFlag : VietnamFlag}
              style={{ 
                cursor: "pointer", 
                objectFit: "cover", 
                borderRadius: "50%",
                transition: "opacity 0.3s",
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
            />
          </Box>
        )}

        <IconButton onClick={() => dispatch(setMode())} >
          {theme.palette.mode === "dark" ? (
            <LightMode sx={{ color: theme.palette.neutral.darkest }} />              
          ) : (
            <DarkMode />
          )}
        </IconButton>          

        {/* <IconButton >
          <Notifications />
        </IconButton> */}

        {!isMobile && (
          <FormControl
            variant="standard"
            sx={{
              border: "1px solid",
              borderColor: theme.palette.neutral.main,
              borderRadius: 1,
            }}
          >
            <Select
              value={fullName}
              sx={{
                width: 260,
                p: "0.2rem 0.5rem",
                borderRadius: 1,
              }}
              input={<InputBase />}
            >
              <MenuItem value={fullName}>
                <Typography>{fullName.substring(0, 30)}</Typography>
              </MenuItem>
              <MenuItem onClick={() => navigate('/settings')} sx={{ gap: 1 }}>
                <Settings fontSize="small" /> Settings
              </MenuItem>
              <MenuItem onClick={handleLogOut} sx={{ gap: 1 }}>
                <Logout fontSize="small" /> Log Out
              </MenuItem>
            </Select>
          </FormControl>
        )}
      </FlexBetween>

    </FlexBetween>
  );
};

export default Navbar;
